/**
 * `MermaidBlockView` — the React NodeView for a `mermaidBlock` (interactive-diagrams-v0.1.md
 * §5.2). This is where a ```mermaid fence becomes interactive inside the Tiptap editor.
 *
 * Three views, switchable via the block toolbar:
 *  - **Canvas** (editable flowcharts): the interactive `DiagramCanvas` — drag tiles, connect
 *    edges. Every change is serialized to mermaid source (minimal-diff) and written back via
 *    `updateAttributes`, which flows through ProseMirror history → **Ctrl+Z undoes a drag**.
 *  - **Rendered**: vanilla mermaid SVG (the layout mermaid itself produces). Always available;
 *    the only view for non-flowchart diagrams (sequence, gantt, …).
 *  - **Source**: a textarea over the raw fence text (incl. the `%% @aletheia` sidecar) for
 *    hand-authoring. Covers everything the canvas can't yet do (delete/relabel) in v0.1.
 *
 * The node's source attribute is the single source of truth; `renderHTML` (in the extension)
 * turns it back into a fence, so the markdown round-trip is byte-stable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { parse } from '../parse';
import { toSvg } from '../exportSvg';
import { renderMermaid } from '../vanillaRender';
import { isGantt, parseGantt } from '../gantt';
import { DiagramCanvas } from './DiagramCanvas';
import { GanttCanvas } from './GanttCanvas';
import './diagram-block.css';

/** Strip a leading `---` YAML frontmatter block so the vanilla-mermaid render doesn't ALSO draw the
 * title (we render `model.title` as a consistent block caption instead). Leaves non-frontmatter
 * sources untouched. */
function stripFrontmatter(source: string): string {
  return source.replace(/^\s*---\r?\n[\s\S]*?\r?\n---[ \t]*\r?\n?/, '');
}

/** Trigger a browser download of a text blob (used for SVG export). */
function downloadText(text: string, filename: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type ViewMode = 'canvas' | 'rendered' | 'source';

/** Read-only vanilla mermaid render into a div (the "Rendered" view + non-flowchart fallback). */
function RenderedMermaid({ source }: { source: string }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let alive = true;
    void renderMermaid(source).then((res) => {
      const el = ref.current;
      if (!alive || !el) return;
      if (res.ok) {
        el.innerHTML = res.svg;
        el.classList.remove('is-error');
      } else {
        el.textContent = `Diagram error: ${res.error}`;
        el.classList.add('is-error');
      }
    });
    return () => {
      alive = false;
    };
  }, [source]);
  return <div className="diagram-rendered" ref={ref} />;
}

/** Raw-source editor. Applies on blur (only if changed) so a stray focus doesn't churn history. */
function SourceEditor({
  source,
  onApply,
  readOnly,
}: {
  source: string;
  onApply: (next: string) => void;
  readOnly: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState(source);
  useEffect(() => setDraft(source), [source]);
  return (
    <textarea
      className="diagram-source"
      value={draft}
      spellCheck={false}
      readOnly={readOnly}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (!readOnly && draft !== source) onApply(draft);
      }}
    />
  );
}

export function MermaidBlockView({ node, updateAttributes, editor, selected }: NodeViewProps): JSX.Element {
  const source = typeof node.attrs.source === 'string' ? node.attrs.source : '';
  const model = useMemo(() => parse(source), [source]);
  const canvasEditable = model.editable && model.nodes.size > 0;
  const canEdit = editor.isEditable;

  // A gantt gets our own timeline canvas (the "Chart" tab) rather than the flowchart canvas.
  const ganttDiagram = useMemo(() => isGantt(source), [source]);
  const ganttSpec = useMemo(() => (ganttDiagram ? parseGantt(source) : null), [ganttDiagram, source]);
  // A "canvas/chart" tab exists for editable flowcharts AND for gantts.
  const hasCanvasTab = canvasEditable || ganttDiagram;

  // Default view: a gantt opens on its Chart timeline. A machine-authored flowchart (no hand-tuned
  // `pos` sidecar) opens in the crisp vanilla-mermaid render; a hand-arranged one opens on the
  // Canvas so the user's layout shows. Everything else renders.
  const hasHandLayout = !!model.sidecar && Object.keys(model.sidecar.pos ?? {}).length > 0;
  const [mode, setMode] = useState<ViewMode>(
    ganttDiagram || (canvasEditable && hasHandLayout) ? 'canvas' : 'rendered',
  );
  // If the diagram type changes under us (source hand-edited away from a canvas type), leave canvas.
  useEffect(() => {
    if (mode === 'canvas' && !hasCanvasTab) setMode('rendered');
  }, [hasCanvasTab, mode]);

  // Fullscreen "maximize" view — editable with the full toolset by default; a "Hide tools" toggle
  // strips all chrome for a clean presentation view. Esc closes (unless a label editor is focused),
  // body scroll is locked while open.
  const [maximized, setMaximized] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const openMaximized = useCallback(() => {
    setPresenting(false);
    setMaximized(true);
  }, []);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      const inField = t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement;
      if (e.key === 'Escape' && !inField) setMaximized(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [maximized]);

  const apply = useCallback(
    (next: string) => {
      updateAttributes({ source: next });
    },
    [updateAttributes],
  );

  const copySource = useCallback(() => {
    void navigator.clipboard?.writeText(source);
  }, [source]);

  const exportSvg = useCallback(() => {
    downloadText(toSvg(model), 'diagram.svg', 'image/svg+xml');
  }, [model]);

  // Buttons live inside a ProseMirror atom — stop mousedown from starting a node drag/selection.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <NodeViewWrapper
      className={`diagram-block${selected ? ' is-selected' : ''}`}
      contentEditable={false}
    >
      <div className="diagram-block__toolbar" onMouseDown={stop}>
        {hasCanvasTab && (
          <button
            type="button"
            className="diagram-block__tab"
            data-active={mode === 'canvas'}
            onClick={() => setMode('canvas')}
          >
            {ganttDiagram ? 'Chart' : 'Canvas'}
          </button>
        )}
        <button
          type="button"
          className="diagram-block__tab"
          data-active={mode === 'rendered'}
          onClick={() => setMode('rendered')}
        >
          Rendered
        </button>
        <button
          type="button"
          className="diagram-block__tab"
          data-active={mode === 'source'}
          onClick={() => setMode('source')}
        >
          Source
        </button>
        <span className="diagram-block__type" title="mermaid diagram type">
          {ganttDiagram ? 'gantt' : model.diagramType === 'unknown' ? 'mermaid' : model.diagramType}
        </span>
        <div className="diagram-block__actions">
          <button type="button" className="diagram-block__tab" onClick={copySource} title="Copy mermaid source">
            Copy
          </button>
          {canvasEditable && (
            <button type="button" className="diagram-block__tab" onClick={exportSvg} title="Download as SVG">
              SVG ⤓
            </button>
          )}
          <button
            type="button"
            className="diagram-block__tab"
            onClick={openMaximized}
            title="Maximize (fullscreen)"
            data-testid="diagram-maximize"
          >
            ⛶ Maximize
          </button>
        </div>
      </div>

      {model.title && !ganttDiagram && <div className="diagram-block__title">{model.title}</div>}

      <div className="diagram-block__body">
        {mode === 'canvas' && ganttDiagram && ganttSpec && (
          <GanttCanvas spec={ganttSpec} onCommit={canEdit ? apply : undefined} />
        )}
        {mode === 'canvas' && !ganttDiagram && canvasEditable && (
          <DiagramCanvas
            model={model}
            readOnly={!canEdit}
            onCommit={canEdit ? apply : undefined}
            onMaximize={openMaximized}
          />
        )}
        {mode === 'rendered' && <RenderedMermaid source={stripFrontmatter(source)} />}
        {mode === 'source' && <SourceEditor source={source} onApply={apply} readOnly={!canEdit} />}
      </div>

      {maximized &&
        createPortal(
          <div className="diagram-fullscreen" role="dialog" aria-modal="true" aria-label="Maximized diagram">
            <div className="diagram-fullscreen__bar">
              <span className="diagram-fullscreen__type">
                {ganttDiagram ? 'gantt' : model.diagramType === 'unknown' ? 'mermaid' : model.diagramType}
              </span>
              {model.title && !ganttDiagram && (
                <span className="diagram-fullscreen__title">{model.title}</span>
              )}
              <span className="diagram-fullscreen__spacer" />
              {mode === 'canvas' && (canvasEditable || ganttDiagram) && (
                <button
                  type="button"
                  className="diagram-block__tab"
                  data-active={presenting}
                  onClick={() => setPresenting((v) => !v)}
                  title={presenting ? 'Show editing tools' : 'Hide tools for presenting'}
                  data-testid="diagram-fullscreen-tools"
                >
                  {presenting ? '🛠 Show tools' : '🖥 Hide tools'}
                </button>
              )}
              <span className="diagram-fullscreen__hint">Esc to close</span>
              <button
                type="button"
                className="diagram-block__tab"
                onClick={() => setMaximized(false)}
                title="Close (Esc)"
                data-testid="diagram-fullscreen-close"
              >
                ✕ Close
              </button>
            </div>
            <div className="diagram-fullscreen__body">
              {mode === 'canvas' && ganttDiagram && ganttSpec ? (
                <GanttCanvas
                  spec={ganttSpec}
                  presenting={presenting}
                  onCommit={canEdit && !presenting ? apply : undefined}
                />
              ) : mode === 'canvas' && canvasEditable ? (
                <DiagramCanvas
                  model={model}
                  readOnly={!canEdit || presenting}
                  onCommit={canEdit && !presenting ? apply : undefined}
                  hideChrome={presenting}
                  fill
                />
              ) : (
                <RenderedMermaid source={stripFrontmatter(source)} />
              )}
            </div>
          </div>,
          document.body,
        )}
    </NodeViewWrapper>
  );
}
